/**
 * La file des scans sur le téléphone (SCAN-QR § 3.3 et § 5.2) : chaque scan y est écrit
 * **avant tout envoi**, et n'en sort qu'après un résultat définitif de la base. Un onglet
 * fermé, un téléphone redémarré, un wifi coupé : la file est relue à la réouverture et repart
 * avec les mêmes clés (la base rend le même résultat pour la même clé).
 *
 * Stockage : IndexedDB (base `cdv-garderie`, magasin `scans`, clé `cle`), chaque écriture
 * résolue seulement quand sa transaction est terminée (`oncomplete`) ; à défaut `localStorage` ;
 * en dernier recours la mémoire de la page, signalée par `persistante: false` (l'écran le dit).
 * Une connexion IndexedDB que le navigateur ferme (WebKit, après une mise en arrière-plan :
 * « Connection to Indexed Database server lost ») est rouverte une fois, puis l'opération
 * refaite. **Aucun nom d'enfant dans la file** : seules les colonnes d'`EntreeDeFile` sont
 * gardées. PROPOSITION TECHNIQUE.
 */
import type { EntreeDeFile } from './scan';

export interface FileDeScans {
  /** Les scans d'une source, les plus anciens d'abord. */
  toutes(source: string): Promise<EntreeDeFile[]>;
  /** Tous les scans du téléphone, toutes sources (un lien renouvelé, une autre école), les plus anciens d'abord. */
  toutesLesSources(): Promise<EntreeDeFile[]>;
  /** Ajoute (ou remplace, même clé) un scan ; résolue une fois l'écriture faite. */
  ajouter(e: EntreeDeFile): Promise<void>;
  /** Remplace tous les scans d'une source (après une réponse de la base). */
  remplacer(source: string, entrees: EntreeDeFile[]): Promise<void>;
  /** Retire des scans par clé. */
  retirer(cles: string[]): Promise<void>;
  /** Faux quand la file ne survit pas à la page (ni IndexedDB, ni `localStorage`). */
  readonly persistante: boolean;
  /** Le stockage retenu. */
  readonly support: 'indexeddb' | 'localstorage' | 'memoire';
}

export const BASE_DE_LA_FILE = 'cdv-garderie';
export const MAGASIN_DE_LA_FILE = 'scans';
export const CLE_LOCALSTORAGE = 'cdv-garderie-scans';

/* ------------------------------------------------------------------------- */
/* Lecture prudente : rien d'autre que les champs de la file                  */
/* ------------------------------------------------------------------------- */

const texte = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const nombre = (v: unknown, defaut: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : defaut);

/** Une entrée relue du stockage, réduite aux champs de la file ; `null` si elle est inutilisable. */
export function lireEntreeDeFile(v: unknown): EntreeDeFile | null {
  if (!v || typeof v !== 'object') return null;
  const x = v as Record<string, unknown>;
  const cle = texte(x.cle);
  const source = texte(x.source);
  const mode = x.mode === 'appel' || x.mode === 'sortie' || x.mode === 'annulation' ? x.mode : null;
  const momentAppareil = texte(x.momentAppareil);
  if (!cle || !source || !mode || !momentAppareil) return null;
  return {
    cle,
    source,
    ecole: texte(x.ecole),
    mode,
    saisie: x.saisie === 'qr' || x.saisie === 'recherche' ? x.saisie : null,
    qr: texte(x.qr),
    enfantId: texte(x.enfantId),
    annuleCle: texte(x.annuleCle),
    plageId: texte(x.plageId),
    momentAppareil,
    ecartMs: nombre(x.ecartMs, 0),
    pointePar: texte(x.pointePar),
    tentatives: nombre(x.tentatives, 0),
    prochainEssai: nombre(x.prochainEssai, 0),
  };
}

const propre = (e: EntreeDeFile): EntreeDeFile => {
  const x = lireEntreeDeFile(e);
  if (!x) throw new Error('[file-de-scans] entrée incomplète : clé, source, mode et moment sont requis');
  return x;
};

const instant = (e: EntreeDeFile): number => {
  const t = Date.parse(e.momentAppareil);
  return Number.isFinite(t) ? t : 0;
};
const parAnciennete = (a: EntreeDeFile, b: EntreeDeFile): number => instant(a) - instant(b) || (a.cle < b.cle ? -1 : a.cle > b.cle ? 1 : 0);

/* ------------------------------------------------------------------------- */
/* En mémoire (tests, dernier recours)                                        */
/* ------------------------------------------------------------------------- */

/** Une file en mémoire : pour les tests, et en dernier recours (`persistante: false`). */
export function fileEnMemoire(): FileDeScans {
  const entrees = new Map<string, EntreeDeFile>();
  return {
    persistante: false,
    support: 'memoire',
    async toutes(source) {
      return [...entrees.values()].filter((e) => e.source === source).map((e) => ({ ...e })).sort(parAnciennete);
    },
    async toutesLesSources() {
      return [...entrees.values()].map((e) => ({ ...e })).sort(parAnciennete);
    },
    async ajouter(e) {
      const x = propre(e);
      entrees.set(x.cle, x);
    },
    async remplacer(source, nouvelles) {
      const propres = nouvelles.map(propre);
      for (const [cle, e] of entrees) if (e.source === source) entrees.delete(cle);
      for (const x of propres) entrees.set(x.cle, x);
    },
    async retirer(cles) {
      for (const c of cles) entrees.delete(c);
    },
  };
}

/* ------------------------------------------------------------------------- */
/* localStorage                                                               */
/* ------------------------------------------------------------------------- */

/** Le sous-ensemble de `Storage` dont la file a besoin. */
export interface StockageSimple {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

/**
 * Une file dans `localStorage` (toutes les sources sous une clé). Chaque écriture relit puis
 * réécrit le tout, de façon synchrone : une écriture refusée (stockage plein) rejette la
 * promesse, pour que l'écran sache que le scan n'est pas gardé.
 */
export function fileDansLeStockage(stockage: StockageSimple, cle: string = CLE_LOCALSTORAGE): FileDeScans {
  const lire = (): EntreeDeFile[] => {
    try {
      const brut = stockage.getItem(cle);
      const v: unknown = brut ? JSON.parse(brut) : [];
      return Array.isArray(v) ? v.map(lireEntreeDeFile).filter((e): e is EntreeDeFile => e !== null) : [];
    } catch {
      return [];
    }
  };
  const ecrire = (entrees: EntreeDeFile[]): void => {
    if (entrees.length === 0) stockage.removeItem(cle);
    else stockage.setItem(cle, JSON.stringify(entrees));
  };
  return {
    persistante: true,
    support: 'localstorage',
    async toutes(source) {
      return lire().filter((e) => e.source === source).sort(parAnciennete);
    },
    async toutesLesSources() {
      return lire().sort(parAnciennete);
    },
    async ajouter(e) {
      const x = propre(e);
      ecrire([...lire().filter((y) => y.cle !== x.cle), x]);
    },
    async remplacer(source, nouvelles) {
      const propres = nouvelles.map(propre);
      const cles = new Set(propres.map((x) => x.cle));
      ecrire([...lire().filter((y) => y.source !== source && !cles.has(y.cle)), ...propres]);
    },
    async retirer(cles) {
      const a = new Set(cles);
      ecrire(lire().filter((y) => !a.has(y.cle)));
    },
  };
}

/* ------------------------------------------------------------------------- */
/* IndexedDB                                                                  */
/* ------------------------------------------------------------------------- */

/** Ce qu'il faut d'IndexedDB (le sous-ensemble utilisé, pour pouvoir l'éprouver sans navigateur). */
export type FabriqueIndexedDB = Pick<IDBFactory, 'open'>;

const enPromesse = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('[file-de-scans] lecture refusée'));
  });

/** Une transaction en écriture, résolue sur `oncomplete` (l'écriture est alors sur le disque). */
const enEcriture = (db: IDBDatabase, travail: (magasin: IDBObjectStore) => void): Promise<void> =>
  new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(MAGASIN_DE_LA_FILE, 'readwrite');
    } catch (e) {
      reject(e);
      return;
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('[file-de-scans] écriture refusée'));
    tx.onabort = () => reject(tx.error ?? new Error('[file-de-scans] écriture abandonnée'));
    try {
      travail(tx.objectStore(MAGASIN_DE_LA_FILE));
    } catch (e) {
      try {
        tx.abort();
      } catch {
        /* déjà finie */
      }
      reject(e);
    }
  });

/** Ouvre la base IndexedDB de la file ; rejette si elle ne s'ouvre pas dans le délai. */
export function ouvrirLaBase(fabrique: FabriqueIndexedDB, delaiMs = 3000): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let fini = false;
    const minuterie = setTimeout(() => {
      if (fini) return;
      fini = true;
      reject(new Error('[file-de-scans] IndexedDB ne répond pas'));
    }, delaiMs);
    let req: IDBOpenDBRequest;
    try {
      req = fabrique.open(BASE_DE_LA_FILE, 1);
    } catch (e) {
      clearTimeout(minuterie);
      fini = true;
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MAGASIN_DE_LA_FILE)) db.createObjectStore(MAGASIN_DE_LA_FILE, { keyPath: 'cle' });
    };
    req.onsuccess = () => {
      clearTimeout(minuterie);
      if (fini) {
        req.result.close(); // ouverte trop tard : on est déjà passé au repli
        return;
      }
      fini = true;
      resolve(req.result);
    };
    req.onerror = () => {
      clearTimeout(minuterie);
      if (fini) return;
      fini = true;
      reject(req.error ?? new Error('[file-de-scans] IndexedDB refusée'));
    };
    req.onblocked = () => {
      /* un autre onglet tient une version plus ancienne : on attend, la minuterie tranche */
    };
  });
}

/**
 * La file sur une base IndexedDB ouverte. `rouvrir` (donné par `ouvrirFileDeScans`) rouvre la
 * base quand le navigateur a fermé la connexion (`onclose`, `onversionchange`, ou une opération
 * qui échoue) : l'opération est refaite une fois sur la base rouverte, puis l'échec remonte
 * (et `avecSecours` passe en mémoire, en le disant).
 */
export function fileIndexedDB(premiere: IDBDatabase, rouvrir?: () => Promise<IDBDatabase>): FileDeScans {
  let db = premiere;
  let perdue = false;
  const suivre = (d: IDBDatabase) => {
    d.onclose = () => {
      if (d === db) perdue = true;
    };
    d.onversionchange = () => {
      try {
        d.close();
      } catch {
        /* déjà fermée */
      }
      if (d === db) perdue = true;
    };
  };
  suivre(db);
  const rouvrirLaBase = async () => {
    if (!rouvrir) return;
    try {
      db.close();
    } catch {
      /* déjà fermée */
    }
    db = await rouvrir();
    perdue = false;
    suivre(db);
  };
  /* L'opération, refaite une fois sur une base rouverte si la connexion est perdue ou si elle échoue. */
  const avecLaBase = async <T>(op: (d: IDBDatabase) => Promise<T>): Promise<T> => {
    if (perdue) await rouvrirLaBase();
    try {
      return await op(db);
    } catch (e) {
      if (!rouvrir) throw e;
      await rouvrirLaBase();
      return op(db);
    }
  };
  const tout = async (d: IDBDatabase): Promise<EntreeDeFile[]> => {
    const tx = d.transaction(MAGASIN_DE_LA_FILE, 'readonly');
    const brut = (await enPromesse(tx.objectStore(MAGASIN_DE_LA_FILE).getAll())) as unknown[];
    return brut.map(lireEntreeDeFile).filter((e): e is EntreeDeFile => e !== null);
  };
  return {
    persistante: true,
    support: 'indexeddb',
    async toutes(source) {
      return avecLaBase(async (d) => (await tout(d)).filter((e) => e.source === source).sort(parAnciennete));
    },
    async toutesLesSources() {
      return avecLaBase(async (d) => (await tout(d)).sort(parAnciennete));
    },
    async ajouter(e) {
      const x = propre(e);
      await avecLaBase((d) =>
        enEcriture(d, (m) => {
          m.put(x);
        }),
      );
    },
    async remplacer(source, nouvelles) {
      const propres = nouvelles.map(propre);
      await avecLaBase(async (d) => {
        const aRetirer = (await tout(d)).filter((y) => y.source === source && !propres.some((x) => x.cle === y.cle)).map((y) => y.cle);
        await enEcriture(d, (m) => {
          for (const c of aRetirer) m.delete(c);
          for (const x of propres) m.put(x);
        });
      });
    },
    async retirer(cles) {
      if (cles.length === 0) return;
      await avecLaBase((d) =>
        enEcriture(d, (m) => {
          for (const c of cles) m.delete(c);
        }),
      );
    },
  };
}

/* ------------------------------------------------------------------------- */
/* Ouverture                                                                  */
/* ------------------------------------------------------------------------- */

export interface EnvironnementDeLaFile {
  indexedDB?: FabriqueIndexedDB | null;
  localStorage?: StockageSimple | null;
  delaiMs?: number;
}

const essayer = <T>(f: () => T): T | null => {
  try {
    return f() ?? null;
  } catch {
    return null;
  }
};

/** Le stockage local utilisable : il faut pouvoir y écrire (Safari privé l'interdit parfois). */
function stockageUtilisable(stockage: StockageSimple | null | undefined): StockageSimple | null {
  if (!stockage) return null;
  try {
    const sonde = `${CLE_LOCALSTORAGE}-sonde`;
    stockage.setItem(sonde, '1');
    stockage.removeItem(sonde);
    return stockage;
  } catch {
    return null;
  }
}

/**
 * Ouvre la file : IndexedDB si elle s'ouvre, sinon `localStorage` s'il accepte d'écrire,
 * sinon la mémoire (`persistante: false`). `env` sert aux tests ; par défaut, le navigateur.
 */
export async function ouvrirFileDeScans(env?: EnvironnementDeLaFile): Promise<FileDeScans> {
  const idb = env && 'indexedDB' in env ? env.indexedDB : essayer(() => (typeof indexedDB !== 'undefined' ? indexedDB : null));
  if (idb) {
    try {
      const delai = env?.delaiMs ?? 3000;
      return fileIndexedDB(await ouvrirLaBase(idb, delai), () => ouvrirLaBase(idb, delai));
    } catch {
      /* repli */
    }
  }
  const ls = stockageUtilisable(env && 'localStorage' in env ? env.localStorage : essayer(() => (typeof localStorage !== 'undefined' ? localStorage : null)));
  if (ls) return fileDansLeStockage(ls);
  return fileEnMemoire();
}
